import {useEffect, useMemo, useRef, useState, type ReactNode} from "react";
import {ChevronRight, Search} from "lucide-react";
import {adminMatchesSearch} from "@/lib/adminSearchText";
import {cn} from "@/lib/utils";
import {Input} from "@/components/ui/input";
import {AdminEmptyState} from "./AdminEmptyState";
import {AdminRowActions} from "./AdminRowActions";

export type AdminTableColumn<T> = {
  key: string;
  header: ReactNode;
  headerTitle?: string;
  className?: string;
  hideBelow?: "md" | "lg";
  render: (item: T) => ReactNode;
};

type Props<T> = {
  items: T[];
  getId: (item: T) => number | string;
  getTitle: (item: T) => ReactNode;
  getSubtitle?: (item: T) => ReactNode;
  getMeta?: (item: T) => ReactNode;
  /** Explicit columns with headers; when set, replaces default ID / Item / Details layout */
  columns?: AdminTableColumn<T>[];
  getSearchText: (item: T) => string;
  selectedId?: number | string | null;
  onRowClick: (item: T) => void;
  /** Optional trailing menu actions; default is chevron open */
  getRowActions?: (item: T) => ReactNode;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchCommit?: () => void;
  /** Hide built-in search when parent toolbar already has search */
  hideSearch?: boolean;
  clientFilter?: boolean;
  className?: string;
  toolbarExtra?: ReactNode;
};

export function AdminModernTable<T>({
  items,
  getId,
  getTitle,
  getSubtitle,
  getMeta,
  columns,
  getSearchText,
  selectedId,
  onRowClick,
  getRowActions,
  loading,
  emptyTitle = "No results",
  emptyDescription,
  emptyAction,
  searchPlaceholder = "Search list...",
  searchValue,
  onSearchChange,
  onSearchCommit,
  hideSearch = false,
  clientFilter = true,
  className,
  toolbarExtra
}: Props<T>) {
  const [localSearch, setLocalSearch] = useState("");
  const query = searchValue ?? localSearch;
  const setQuery = onSearchChange ?? setLocalSearch;
  const onSearchCommitRef = useRef(onSearchCommit);
  onSearchCommitRef.current = onSearchCommit;

  useEffect(() => {
    if(!onSearchCommitRef.current) return;
    const t = setTimeout(() => onSearchCommitRef.current?.(), 350);
    return () => clearTimeout(t);
  }, [query]);

  const visibleItems = useMemo(() => {
    if(!clientFilter || !query.trim()) return items;
    return items.filter((item) => adminMatchesSearch(query, getSearchText(item)));
  }, [items, query, clientFilter, getSearchText]);

  if(loading) {
    return (
      <div className={cn("admin-modern-table admin-modern-table--loading p-6", className)}>
        <div className="space-y-3">
          {Array.from({length: 5}).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-muted/60" />
          ))}
        </div>
      </div>
    );
  }

  // When search lives in AdminFilterBar (hideSearch), skip the second card toolbar.
  // Keep a slim meta strip only if toolbarExtra is provided or clientFilter needs a live "found" count.
  const showFullToolbar = !hideSearch || Boolean(toolbarExtra);
  const showMetaOnly = hideSearch && !toolbarExtra && clientFilter;

  return (
    <div className={cn("admin-modern-table", className)}>
      {showFullToolbar ? (
        <div className="admin-modern-table__toolbar">
          {!hideSearch ? (
            <div className="relative min-w-0 flex-1 sm:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-10 border-border/70 bg-background pl-9"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-3">
            {toolbarExtra}
            <p className="admin-caption tabular-nums">
              {visibleItems.length} {visibleItems.length === 1 ? "item" : "items"}
              {query.trim() ? " found" : ""}
            </p>
          </div>
        </div>
      ) : showMetaOnly ? (
        <div className="admin-modern-table__toolbar admin-modern-table__toolbar--meta">
          <p className="admin-caption tabular-nums">
            {visibleItems.length} {visibleItems.length === 1 ? "item" : "items"}
            {query.trim() ? " found" : ""}
          </p>
        </div>
      ) : null}

      {!visibleItems.length ? (
        <AdminEmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      ) : columns?.length ? (
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      col.className,
                      col.hideBelow === "md" && "hidden md:table-cell",
                      col.hideBelow === "lg" && "hidden lg:table-cell"
                    )}
                    title={col.headerTitle}
                  >
                    {col.header}
                  </th>
                ))}
                <th className={cn("text-right", getRowActions ? "min-w-[7.5rem]" : "w-[72px]")} aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => {
                const id = getId(item);
                const active = selectedId != null && selectedId === id;
                return (
                  <tr
                    key={String(id)}
                    data-active={active ? "true" : undefined}
                    onClick={() => onRowClick(item)}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          col.className,
                          col.hideBelow === "md" && "hidden md:table-cell",
                          col.hideBelow === "lg" && "hidden lg:table-cell"
                        )}
                      >
                        {col.render(item)}
                      </td>
                    ))}
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {getRowActions ? (
                          getRowActions(item)
                        ) : (
                          <span className="admin-modern-table__chevron" aria-hidden>
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th className="w-[88px]">ID</th>
                <th>Item</th>
                <th className="hidden md:table-cell">Details</th>
                <th className={cn("text-right", getRowActions ? "min-w-[7.5rem]" : "w-[72px]")} aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => {
                const id = getId(item);
                const active = selectedId != null && selectedId === id;
                return (
                  <tr
                    key={String(id)}
                    data-active={active ? "true" : undefined}
                    onClick={() => onRowClick(item)}
                  >
                    <td>
                      <span className="admin-modern-table__id">#{id}</span>
                    </td>
                    <td>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">{getTitle(item)}</div>
                        {getSubtitle ? (
                          <div className="mt-1 truncate text-xs text-muted-foreground">{getSubtitle(item)}</div>
                        ) : null}
                      </div>
                    </td>
                    <td className="hidden md:table-cell">
                      {getMeta ? (
                        <div className="flex flex-wrap items-center gap-1.5">{getMeta(item)}</div>
                      ) : null}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="flex flex-wrap items-center justify-end gap-1.5 md:hidden">
                          {getMeta?.(item)}
                        </div>
                        {getRowActions ? (
                          getRowActions(item)
                        ) : (
                          <span className="admin-modern-table__chevron" aria-hidden>
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export {AdminRowActions};
